import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import {
  createMicrosoftTeamsTestMeeting,
  disconnectMicrosoftTeams,
  getMicrosoftTeamsSettings,
  startMicrosoftTeamsOAuth,
} from '../services/microsoftTeamsService';
import { MicrosoftTeamsMeeting, MicrosoftTeamsSettings } from '../types/microsoftTeams';

interface TeamsConnectorTabProps {
  canManage: boolean;
  onConnectionChange?: (connected: boolean) => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const TeamsConnectorTab: React.FC<TeamsConnectorTabProps> = ({ canManage, onConnectionChange }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [connecting, setConnecting] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [creatingMeeting, setCreatingMeeting] = React.useState(false);
  const [settings, setSettings] = React.useState<MicrosoftTeamsSettings | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [testMeeting, setTestMeeting] = React.useState<MicrosoftTeamsMeeting | null>(null);

  const applySettings = React.useCallback((next: MicrosoftTeamsSettings) => {
    setSettings(next);
    onConnectionChange?.(next.connected);
  }, [onConnectionChange]);

  const loadSettings = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getMicrosoftTeamsSettings();
      applySettings(data);
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : t('failedToLoadMicrosoftTeams');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [applySettings, t]);

  React.useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('teamsOAuth');
    if (!oauthStatus) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('teamsOAuth');
    window.history.replaceState({}, '', url.toString());

    if (oauthStatus === 'success') {
      showToast(t('microsoftTeamsOAuthSuccess'), 'success');
      void loadSettings();
      return;
    }

    showToast(t('microsoftTeamsOAuthFailed'), 'error');
  }, [loadSettings, showToast, t]);

  const connect = async () => {
    setConnecting(true);
    setError(null);

    try {
      const result = await startMicrosoftTeamsOAuth();
      if (!result.authorizeUrl) {
        throw new Error(t('microsoftTeamsOAuthFailed'));
      }

      window.location.assign(result.authorizeUrl);
    } catch (connectError) {
      const message = connectError instanceof Error ? connectError.message : t('microsoftTeamsOAuthFailed');
      setError(message);
      showToast(message, 'error');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t('microsoftTeamsDisconnectConfirm'))) {
      return;
    }

    setDisconnecting(true);
    setError(null);
    setTestMeeting(null);

    try {
      const next = await disconnectMicrosoftTeams();
      applySettings(next);
      showToast(t('microsoftTeamsDisconnected'), 'success');
    } catch (disconnectError) {
      const message = disconnectError instanceof Error ? disconnectError.message : t('microsoftTeamsOAuthFailed');
      setError(message);
      showToast(message, 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const createTestMeeting = async () => {
    setCreatingMeeting(true);
    setError(null);

    try {
      const meeting = await createMicrosoftTeamsTestMeeting();
      setTestMeeting(meeting);
      showToast(t('microsoftTeamsTestMeetingCreated'), 'success');
    } catch (meetingError) {
      const message = meetingError instanceof Error ? meetingError.message : t('failedToCreateTeamsMeeting');
      setError(message);
      showToast(message, 'error');
    } finally {
      setCreatingMeeting(false);
    }
  };

  const copyJoinUrl = async (joinUrl: string) => {
    await navigator.clipboard.writeText(joinUrl);
    showToast(t('microsoftTeamsJoinUrlCopied'), 'success');
  };

  const connectedName = settings?.connectedDisplayName || settings?.connectedUserPrincipalName;
  const busy = connecting || disconnecting || creatingMeeting;

  return (
    <section className="company-section">
      <div className="company-section-head">
        <div>
          <h3>{t('microsoftTeamsHeading')}</h3>
          <p>{t('microsoftTeamsHelp')}</p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingMicrosoftTeamsSettings')} />
        </div>
      ) : (
        <>
          {error ? (
            <div className="error" style={{ marginTop: 12 }}>{error}</div>
          ) : null}

          {settings?.connected ? (
            <div className="telegram-status-grid" style={{ marginTop: 16 }}>
              <div className="telegram-status-card">
                <div className="telegram-status-card-title">{t('microsoftTeamsConnectedAs')}</div>
                <dl className="telegram-status-list">
                  <div>
                    <dt>{t('microsoftTeamsAccount')}</dt>
                    <dd>{connectedName || t('telegramStatusNotAvailable')}</dd>
                  </div>
                  {settings.connectedUserPrincipalName && settings.connectedDisplayName ? (
                    <div>
                      <dt>{t('microsoftTeamsEmail')}</dt>
                      <dd>{settings.connectedUserPrincipalName}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>{t('microsoftTeamsConnectedAt')}</dt>
                    <dd>{formatDateTime(settings.connectedAt)}</dd>
                  </div>
                  {settings.azureTenantId ? (
                    <div>
                      <dt>{t('microsoftTeamsAzureTenant')}</dt>
                      <dd>{settings.azureTenantId}</dd>
                    </div>
                  ) : null}
                </dl>
                {canManage ? (
                  <div className="company-actions-inline" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void connect()}
                      disabled={busy}
                    >
                      {connecting ? t('microsoftTeamsConnecting') : t('microsoftTeamsReconnect')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void disconnect()}
                      disabled={busy}
                    >
                      {disconnecting ? t('savingEllipsis', { ns: 'admin' }) : t('microsoftTeamsDisconnect')}
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="telegram-status-card">
                <div className="telegram-status-card-title">{t('microsoftTeamsCreateTestMeeting')}</div>
                <p style={{ color: '#516173', fontSize: 13, margin: '0 0 12px' }}>
                  {t('microsoftTeamsTestMeetingHint')}
                </p>
                {canManage ? (
                  <div className="company-actions-inline">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => void createTestMeeting()}
                      disabled={busy}
                    >
                      {creatingMeeting ? t('microsoftTeamsCreatingMeeting') : t('microsoftTeamsCreateTestMeeting')}
                    </button>
                  </div>
                ) : null}
                {testMeeting?.joinUrl ? (
                  <dl className="telegram-status-list" style={{ marginTop: 16 }}>
                    <div>
                      <dt>{t('microsoftTeamsJoinUrl')}</dt>
                      <dd>
                        <a href={testMeeting.joinUrl} target="_blank" rel="noreferrer">
                          {testMeeting.joinUrl}
                        </a>
                      </dd>
                    </div>
                  </dl>
                ) : null}
                {testMeeting?.joinUrl && canManage ? (
                  <div className="company-actions-inline" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void copyJoinUrl(testMeeting.joinUrl)}
                    >
                      {t('microsoftTeamsCopyJoinUrl')}
                    </button>
                    <a
                      className="btn btn-secondary"
                      href={testMeeting.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('microsoftTeamsOpenMeeting')}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              <p style={{ color: '#516173', fontSize: 13, margin: '0 0 12px' }}>
                {settings?.oauthConfigured
                  ? t('microsoftTeamsSharedMailboxHint')
                  : t('microsoftTeamsNotConfigured')}
              </p>
              {canManage && settings?.oauthConfigured ? (
                <div className="company-actions-inline">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void connect()}
                    disabled={busy}
                  >
                    {connecting ? t('microsoftTeamsConnecting') : t('microsoftTeamsConnect')}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </section>
  );
};
